import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { type RTPSideResults } from '@/types/rtpDownload';
import { type Patient, getPatientFullName } from '@/types/patient';
import { usePagedSelection } from '@/hooks/usePagedSelection';

interface RTPPreviewTableProps {
  previewData: Map<string, RTPSideResults>;
  patients: Patient[];
  selectedPatientIds: Set<string>;
  onTogglePatient: (patientId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

interface PatientRTPCounts {
  left: number;
  right: number;
  hasData: boolean;
}

function getPatientRTPCounts(
  patientId: string,
  previewData: Map<string, RTPSideResults>
): PatientRTPCounts {
  const data = previewData.get(patientId);
  if (!data) return { left: 0, right: 0, hasData: false };

  const leftCount = data.left.length;
  const rightCount = data.right.length;

  return {
    left: leftCount,
    right: rightCount,
    hasData: leftCount > 0 || rightCount > 0,
  };
}

export function RTPPreviewTable({
  previewData,
  patients,
  selectedPatientIds,
  onTogglePatient,
  onSelectAll,
  onDeselectAll,
}: RTPPreviewTableProps) {
  // Filter to only patients with data
  const patientsWithData = Array.from(previewData.keys())
    .map((patientId) => ({
      patientId,
      patient: patients.find((p) => p.id === patientId),
      counts: getPatientRTPCounts(patientId, previewData),
    }))
    .filter((item) => item.patient && item.counts.hasData);

  const {
    displayedItems: displayedPatients,
    displayCount,
    hasMore,
    loadMore,
    allSelected,
    someSelected,
  } = usePagedSelection({
    items: patientsWithData,
    selectedIds: selectedPatientIds,
    getId: (item) => item.patientId,
  });

  const handleToggleAll = () => {
    if (allSelected) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted" data-testid="rtp-preview-summary">
          Showing {Math.min(displayCount, patientsWithData.length)} of {patientsWithData.length}{' '}
          patients with RTP data
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-border" data-testid="rtp-preview-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleToggleAll}
                  aria-label="Select all patients"
                  data-testid="rtp-preview-select-all"
                />
              </TableHead>
              <TableHead>Patient ID</TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead className="text-right">Left RTPs</TableHead>
              <TableHead className="text-right">Right RTPs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedPatients.map(({ patientId, patient, counts }) => {
              if (!patient) return null;

              const isSelected = selectedPatientIds.has(patientId);
              return (
                <TableRow key={patientId} className={!isSelected ? 'opacity-50' : ''}>
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onTogglePatient(patientId)}
                      aria-label={`Select patient ${getPatientFullName(patient)}`}
                      data-testid={`rtp-preview-select-${patientId}`}
                    />
                  </TableCell>
                  <TableCell
                    className="font-mono text-sm text-muted"
                    data-testid={`rtp-preview-username-${patientId}`}
                  >
                    {patient.username}
                  </TableCell>
                  <TableCell>{getPatientFullName(patient)}</TableCell>
                  <TableCell
                    className="text-right font-mono text-sm"
                    data-testid={`rtp-preview-left-count-${patientId}`}
                  >
                    {counts.left.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className="text-right font-mono text-sm"
                    data-testid={`rtp-preview-right-count-${patientId}`}
                  >
                    {counts.right.toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={loadMore} data-testid="rtp-preview-load-more">
            Load More ({patientsWithData.length - displayCount} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
